import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, LogOut, Pencil, Search } from "lucide-react";
import { api, type Me, type ViewAsInfo } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog } from "../components/ui/overlay";
import { Input, Label } from "../components/ui/primitives";

// Staff "view as" someone (the worker's viewAs.ts and access.ts). Starting or ending a view reloads
// the app, so nothing cached for one person is ever shown as another's.

const textarea =
  "w-full rounded-lg border border-border-strong bg-surface px-2.5 py-1.5 text-sm outline-none placeholder:text-subtle focus:border-accent focus:ring-2 focus:ring-accent/20";

/** Why the last view ended, from the reload that ended it. */
export type ViewAsEnded = "ended" | "expired" | "exited";

export const leaveViewAs = (why: ViewAsEnded) => window.location.assign(`/?viewas=${why}`);

const plural = (n: number, what: string) => `${n} ${what}${n === 1 ? "" : "s"}`;

/** The strip across the top while viewing as someone: who, how, for how long, and the way out. */
export function ViewAsBanner({ me, viewAs }: { me: Me; viewAs: ViewAsInfo }) {
  const [now, setNow] = useState(() => Date.now());
  const [editOpen, setEditOpen] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);
  const left = Date.parse(viewAs.expiresAt) - now;
  const exit = useMutation({ mutationFn: api.exitViewAs, onSettled: (_r, _e, why: ViewAsEnded) => leaveViewAs(why) });
  const { mutate: exitNow } = exit;
  const over = left <= 0;
  useEffect(() => {
    if (over) exitNow("expired");
  }, [over, exitNow]);

  return (
    <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-warn-fg/25 bg-warn-bg px-6 py-2.5 text-sm text-warn-fg">
      <Eye className="size-4 shrink-0" />
      <span className="min-w-0 flex-[1_1_16rem]">
        Viewing as <span className="font-semibold">{me.name ?? me.userId}</span>
        {viewAs.kind === "staff" && " (staff)"} · {plural(me.locationIds.length, "location")} ·{" "}
        {viewAs.editing ? <span className="font-medium">Editing: changes are made to their account</span> : "Read-only"} · ends in{" "}
        {Math.max(1, Math.ceil(left / 60_000))}m
      </span>
      {!viewAs.editing && (
        <Button size="sm" onClick={() => setEditOpen(true)}>
          <Pencil className="size-3" /> Enable editing
        </Button>
      )}
      <Button size="sm" variant="primary" disabled={exit.isPending} onClick={() => exit.mutate("exited")}>
        <LogOut className="size-3" /> Exit
      </Button>
      <Dialog open={editOpen} onOpenChange={setEditOpen} title="Enable editing" className="w-[480px] max-w-[calc(100vw-32px)] p-6">
        <EnableEditing name={me.name ?? me.userId} onDone={() => setEditOpen(false)} />
      </Dialog>
    </div>
  );
}

function EnableEditing({ name, onDone }: { name: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const enable = useMutation({
    mutationFn: () => api.enableViewAsEdit(reason.trim()),
    onSuccess: () => {
      // Same person, same data: just refetch what's editable now.
      void qc.invalidateQueries();
      onDone();
    },
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); enable.mutate(); }}>
      <h2 className="text-base font-semibold tracking-tight">Edit on {name}'s behalf</h2>
      <p className="mt-1 text-sm text-muted">
        You'll be able to change, create and delete their dashboards and widgets until this view ends. Changes look like theirs; the reason and
        every change are recorded in the audit log under your name.
      </p>
      <Label htmlFor="view-as-reason" className="mt-4 block">Reason</Label>
      <textarea
        id="view-as-reason" autoFocus rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="A ticket link, or what you're fixing" className={`mt-1 ${textarea}`}
      />
      <div className="mt-5 flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onDone}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={reason.trim().length < 3 || enable.isPending}>Enable editing</Button>
      </div>
      {enable.error && <p className="mt-2 text-sm text-bad">{enable.error.message}</p>}
    </form>
  );
}

/** Pick someone who has signed in here to view as. */
export function ViewAsPicker({ tier }: { tier: NonNullable<Me["viewAsTier"]> }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const results = useQuery({ queryKey: ["view-as-users", debounced], queryFn: () => api.viewAsUsers(debounced), retry: false });
  const start = useMutation({ mutationFn: api.startViewAs, onSuccess: () => window.location.assign("/") });

  return (
    <>
      <h1 className="text-xl font-semibold tracking-tight">View as someone</h1>
      <p className="mt-2 text-sm text-muted">
        See exactly what {tier === "super" ? "a customer or staff member" : "a customer"} sees: their dashboards, shared dashboards and locations.
        Read-only unless you turn on editing, for up to an hour. Views are logged.
      </p>
      <div className="relative mt-6">
        <Search className="pointer-events-none absolute left-2.5 top-2 size-4 text-subtle" />
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, user ID or location ID" className="pl-8" />
      </div>
      <div className="mt-2 max-h-80 overflow-y-auto">
        {!debounced && results.data?.length ? <p className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-subtle">Recently signed in</p> : null}
        {results.data?.map((u) => (
          <button
            key={u.id}
            disabled={start.isPending}
            onClick={() => start.mutate(u.id)}
            className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-surface-2 disabled:opacity-60"
          >
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-accent">
              {u.name.slice(0, 1).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm">{u.name}</span>
              <span className="block truncate text-xs text-subtle">
                {[u.kind === "staff" ? "Staff" : "Customer", u.email, u.kind === "customer" && `#${u.id}`, plural(u.locationCount, "location"),
                  `last seen ${new Date(u.lastSeenAt).toLocaleDateString()}`].filter(Boolean).join(" · ")}
              </span>
            </span>
          </button>
        ))}
        {results.isSuccess && !results.data.length && (
          <p className="px-2 py-2 text-sm text-subtle">{debounced ? "No one matching has signed in here." : "No one has signed in here yet."}</p>
        )}
        {results.error && <p className="px-2 py-2 text-sm text-bad">{results.error.message}</p>}
      </div>
      {start.error && <p className="mt-2 text-sm text-bad">{start.error.message}</p>}
    </>
  );
}

/** After a view ends, a one-time note saying so (read from the URL the ending reloaded to). */
export function useViewAsEnded(): [ViewAsEnded | null, () => void] {
  const [why, setWhy] = useState<ViewAsEnded | null>(() => {
    const v = new URLSearchParams(window.location.search).get("viewas");
    return v === "ended" || v === "expired" || v === "exited" ? v : null;
  });
  return [why, () => setWhy(null)];
}

export const VIEW_AS_ENDED_TEXT: Record<ViewAsEnded, string> = {
  exited: "You're back to your own view.",
  expired: "Your view as someone reached its one-hour limit. You're back to your own view.",
  ended: "Your view as someone ended because their access (or yours) changed. You're back to your own view.",
};
